<template>
  <modal v-if="eligible" :title="screenTitle" :form="formName" :forceShowSave="true" @close="$emit('close')"
    @save="validate">
    <template slot="body">
      <div class="modal-container">
        <VueTabs v-model="activeTab">
          <!-- Tab 1 - General Information -->
          <v-tab id="tab0" :title="$t('bridge.general_information')">
            <div class="md-layout bridges-form" style="margin-top: 24px;">
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field>
                  <label>{{ $t('bridges_table.region_name') }}</label>
                  <md-input v-model="gi_fk_region_oblast" type="text" maxlength="100" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field>
                  <label>{{ $t('bridge.dep') }}</label>
                  <md-input v-model="gi_fk_deu" type="text" maxlength="100" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field>
                  <label>{{ $t('tunnel.road_name') }}</label>
                  <md-input v-model="gi_fk_road_route" type="text" maxlength="100" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field>
                  <label>{{ $t('bridge.road_class') }}</label>
                  <md-input v-model="gi_road_class" type="number" step="1" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field>
                  <label>{{ $t('bridge.structural_soundness') }}</label>
                  <md-select v-model="gi_fk_structural_soundness">
                    <md-option :value="option.id" v-for="option in gi_structuralOptions" :key="option.id">
                      {{ option.name }}
                    </md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field>
                  <label>{{ $t('bridge.bridge_num') }}</label>
                  <md-input v-model="gi_bridge_num" type="number" step="1" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field>
                  <label>{{ $t('bridge.location') }}</label>
                  <md-input v-model="gi_location" type="text" maxlength="20" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field :class="{ 'md-invalid': gi_year_construction && !isYearValid }">
                  <label>{{ $t('bridge.year_construction') }}</label>
                  <md-input v-model="gi_year_construction" type="number" step="1" min="1900" max="2100" />
                  <span v-if="gi_year_construction && yearErrorMessage" class="md-error">{{ yearErrorMessage }}</span>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field :class="{ 'md-invalid': gi_latitude_n && !isLatitudeValid }">
                  <label>{{ $t('bridge.latitude_n') }}</label>
                  <md-input v-model="gi_latitude_n" type="number" step="any" />
                  <span v-if="gi_latitude_n && latitudeErrorMessage" class="md-error">{{ latitudeErrorMessage }}</span>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field :class="{ 'md-invalid': gi_longitude_e && !isLongitudeValid }">
                  <label>{{ $t('bridge.longitude_e') }}</label>
                  <md-input v-model="gi_longitude_e" type="number" step="any" />
                  <span v-if="gi_longitude_e && longitudeErrorMessage" class="md-error">{{ longitudeErrorMessage }}</span>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field>
                  <label>{{ $t('bridge.design_load') }}</label>
                  <md-input v-model="gi_design_load" type="text" maxlength="10" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-40">
                <md-field>
                  <label>{{ $t('bridge.feature') }}</label>
                  <md-input v-model="gi_feature" type="text" maxlength="100" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-100 md-size-80">
                <md-field>
                  <label>{{ $t('bridge.attachment') }}</label>
                  <md-input v-model="gi_attachment" type="text" maxlength="100" />
                </md-field>
              </div>
            </div>
          </v-tab>

          <!-- Tab 2 - Dimensions -->
          <v-tab id="tab1" :title="$t('bridge.dimensions')">
            <div class="md-layout bridges-form" style="margin-top: 24px;">
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridge.bridge_length') }}</label>
                  <md-input v-model="d_dim_bridge_length" type="number" step="any" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridge.roadway_width') }}</label>
                  <md-input v-model="d_dim_width_roadway" type="number" step="any" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridge.sidewalk_width') }}</label>
                  <md-input v-model="d_dim_width_sidewalk" type="number" step="any" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridge.num_of_span') }}</label>
                  <md-input v-model="d_num_of_span" type="number" step="1" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridges_table.bridge_length_count') }}</label>
                  <md-input v-model="d_bridge_length_count" type="number" step="1" min="0" />
                </md-field>
              </div>
            </div>
          </v-tab>

          <!-- Tab 3 - Materials -->
          <v-tab id="tab2" :title="$t('bridge.materials')">
            <div class="md-layout bridges-form" style="margin-top: 24px;">
              <!-- Superstructure 1 Row -->
              <div class="md-layout-item md-small-size-100 md-medium-size-100 md-size-12">
                <div style="padding: 16px 4px 8px 4px; font-weight: 500; font-size: 14px;">{{ $t('bridge.superstructure 1') }}</div>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.material') }}</label>
                  <md-select v-model="m_ss1_material">
                    <md-option :value="opt.id" v-for="opt in materialOptions" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.structural_type_1') }}</label>
                  <md-select v-model="m_ss1_structural_type_1">
                    <md-option :value="opt.id" v-for="opt in structuralType1Options" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.structural_type_2') }}</label>
                  <md-select v-model="m_ss1_structural_type_2">
                    <md-option :value="opt.id" v-for="opt in structuralType2Options" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.num_of_girder') }}</label>
                  <md-input v-model="m_ss1_num_of_girder" type="number" step="1" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.num_of_slab') }}</label>
                  <md-input v-model="m_ss1_num_of_slab" type="number" step="1" min="0" />
                </md-field>
              </div>

              <!-- Superstructure 2 Row -->
              <div class="md-layout-item md-small-size-100 md-medium-size-100 md-size-12">
                <div style="padding: 16px 4px 8px 4px; font-weight: 500; font-size: 14px;">{{ $t('bridge.superstructure 2') }}</div>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.material') }}</label>
                  <md-select v-model="m_ss2_material">
                    <md-option :value="opt.id" v-for="opt in materialOptions" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.structural_type_1') }}</label>
                  <md-select v-model="m_ss2_structural_type_1">
                    <md-option :value="opt.id" v-for="opt in structuralType1Options" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.structural_type_2') }}</label>
                  <md-select v-model="m_ss2_structural_type_2">
                    <md-option :value="opt.id" v-for="opt in structuralType2Options" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.num_of_girder') }}</label>
                  <md-input v-model="m_ss2_num_of_girder" type="number" step="1" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.num_of_slab') }}</label>
                  <md-input v-model="m_ss2_num_of_slab" type="number" step="1" min="0" />
                </md-field>
              </div>

              <!-- Superstructure 3 Row -->
              <div class="md-layout-item md-small-size-100 md-medium-size-100 md-size-12">
                <div style="padding: 16px 4px 8px 4px; font-weight: 500; font-size: 14px;">{{ $t('bridge.superstructure 3') }}</div>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.material') }}</label>
                  <md-select v-model="m_ss3_material">
                    <md-option :value="opt.id" v-for="opt in materialOptions" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.structural_type_1') }}</label>
                  <md-select v-model="m_ss3_structural_type_1">
                    <md-option :value="opt.id" v-for="opt in structuralType1Options" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.structural_type_2') }}</label>
                  <md-select v-model="m_ss3_structural_type_2">
                    <md-option :value="opt.id" v-for="opt in structuralType2Options" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.num_of_girder') }}</label>
                  <md-input v-model="m_ss3_num_of_girder" type="number" step="1" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-17-6">
                <md-field>
                  <label>{{ $t('bridge.num_of_slab') }}</label>
                  <md-input v-model="m_ss3_num_of_slab" type="number" step="1" min="0" />
                </md-field>
              </div>

              <!-- Separator -->
              <div class="md-layout-item md-small-size-100 md-medium-size-100 md-size-100" style="margin-top: 20px;">
                <hr style="border: 0; border-top: 1px solid #ccc; margin: 16px 0;">
              </div>

              <!-- Substructure -->
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-50">
                <md-field>
                  <label>{{ $t('bridge.sub_material') }}</label>
                  <md-select v-model="m_sub_material">
                    <md-option :value="opt.id" v-for="opt in materialOptions" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-50">
                <md-field>
                  <label>{{ $t('bridge.sub_structural_type_of_pier') }}</label>
                  <md-select v-model="m_sub_structural_type_of_pier">
                    <md-option :value="opt.id" v-for="opt in structuralPierOptions" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-50">
                <md-field>
                  <label>{{ $t('bridge.sub_height_of_substructure') }}</label>
                  <md-input v-model="m_sub_height_of_substructure" type="number" step="any" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-50">
                <md-field>
                  <label>{{ $t('bridge.sub_num_of_substructure') }}</label>
                  <md-input v-model="m_sub_num_of_substructure" type="number" step="1" min="0" />
                </md-field>
              </div>
            </div>
          </v-tab>

          <!-- Tab 4 - Other parts -->
          <v-tab id="tab3" :title="$t('bridge.other_parts')">
            <div class="md-layout bridges-form" style="margin-top: 24px;">
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridge.bearing') }} - {{ $t('bridge.other_with_or_without') }}</label>
                  <md-input v-model="bearing_with_or_without" type="number" step="1" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridge.bearing') }} - {{ $t('bridge.other_material') }}</label>
                  <md-select v-model="bearing_material">
                    <md-option :value="opt.id" v-for="opt in materialOptions" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-100 md-size-33">
                <!-- Spacer for alignment -->
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridge.ej') }} - {{ $t('bridge.other_with_or_without') }}</label>
                  <md-input v-model="ej_with_or_without" type="number" step="1" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridge.ej') }} - {{ $t('bridge.other_material') }}</label>
                  <md-select v-model="ej_material">
                    <md-option :value="opt.id" v-for="opt in materialOptions" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-100 md-size-33">
                <!-- Spacer for alignment -->
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridge.railing') }} - {{ $t('bridge.other_with_or_without') }}</label>
                  <md-input v-model="railing_with_or_without" type="number" step="1" min="0" />
                </md-field>
              </div>
              <div class="md-layout-item md-small-size-100 md-medium-size-50 md-size-33">
                <md-field>
                  <label>{{ $t('bridge.railing') }} - {{ $t('bridge.other_material') }}</label>
                  <md-select v-model="railing_material">
                    <md-option :value="opt.id" v-for="opt in materialOptions" :key="opt.id">{{ opt.name }}</md-option>
                  </md-select>
                </md-field>
              </div>
            </div>
          </v-tab>
        </VueTabs>
      </div>
    </template>
  </modal>
</template>

<script>
import { VueTabs, VTab } from 'vue-nav-tabs'
import 'vue-nav-tabs/themes/vue-tabs.css'
import { Modal, RdbLookupDropdown } from '@/pages/Components'
import BridgesApi from '@/api/bridges_tonnels'
import { mapActions } from 'vuex'
import { savedMessage, errorMessage } from '@/mixins/messagesMixin'

export default {
  name: 'bridges-table-edit-form',
  data() {
    return {
      formName: 'BridgesTableEditForm',
      eligible: false,
      // Tab 1 - General Information
      gi_fk_region_oblast: null, // Dropdown | now Textbox
      gi_fk_deu: null, // Dropdown | now Textbox
      gi_fk_structural_soundness: null, // Dropdown | numeric
      gi_structuralOptions: [], // Dropdown
      gi_maintenance_category: null, // Textbox
      gi_bridge_num: null, // Textbox | numeric
      gi_road_class: null, // Textbox | numeric
      gi_year_construction: null, // Textbox | numeric
      gi_fk_road_route: null, // Dropdown | now Textbox
      gi_location: null, // Textbox | String
      gi_longitude_e: null, // Textbox | numeric
      gi_latitude_n: null, // Textbox | numeric
      gi_design_load: null, // Textbox | String
      gi_feature: null, // Textbox | String
      gi_attachment: null, // Textbox | String

      // Tab 2 - Dimensions
      d_dim_bridge_length: null, // Textbox | numeric
      d_bridge_length_count: null, // Textbox | numeric
      d_dim_width_roadway: null, // Textbox | numeric
      d_dim_width_sidewalk: null, // Textbox | numeric
      d_num_of_span: null, // Textbox | numeric

      // Tab 3 - Materials
      m_ss1_material: null, // Dropdown | numeric
      m_ss1_structural_type_1: null, // Dropdown | numeric
      m_ss1_structural_type_2: null, // Dropdown | numeric
      m_ss1_num_of_girder: null, // Textbox | numeric
      m_ss1_num_of_slab: null, // Textbox | numeric
      m_ss2_material: null, // Dropdown | numeric
      m_ss2_structural_type_1: null, // Dropdown | numeric
      m_ss2_structural_type_2: null, // Dropdown | numeric
      m_ss2_num_of_girder: null, // Textbox | numeric
      m_ss2_num_of_slab: null, // Textbox | numeric
      m_ss3_material: null, // Dropdown | numeric
      materialOptions: [],
      structuralType1Options: [],
      structuralType2Options: [],
      m_ss3_structural_type_1: null, // Dropdown | numeric
      m_ss3_structural_type_2: null, // Dropdown | numeric
      m_ss3_num_of_girder: null, // Textbox | numeric
      m_ss3_num_of_slab: null, // Textbox | numeric
      m_sub_material: null, // Dropdown | numeric
      m_sub_structural_type_of_pier: null, // Dropdown | numeric
      structuralPierOptions: [],
      m_sub_height_of_substructure: null, // Textbox | numeric
      m_sub_num_of_substructure: null, // Textbox | numeric

      // Tab 4 - Other parts
      bearing_with_or_without: null, // Textbox | numeric
      bearing_material: null, // Dropdown | numeric
      ej_with_or_without: null, // Textbox | numeric
      ej_material: null, // Dropdown | numeric
      railing_with_or_without: null, // Textbox | numeric
      railing_material: null, // Dropdown | numeric

      initialFormState: null,

      // All fields, required: false
      modelValidations: {
        gi_fk_region_oblast: { required: false },
        gi_fk_deu: { required: false },
        gi_fk_structural_soundness: { required: false },
        gi_maintenance_category: { required: false },

        gi_bridge_num: { required: false },
        gi_road_class: { required: false },
        gi_year_construction: { required: false, min_value: 2000, max_value: 2100 },
        gi_fk_road_route: { required: false },
        gi_location: { required: false },
        gi_longitude_e: { required: false },
        gi_latitude_n: { required: false },
        gi_design_load: { required: false },
        gi_feature: { required: false },
        gi_attachment: { required: false },

        d_dim_bridge_length: { required: false },
        d_bridge_length_count: { required: false },
        d_dim_width_roadway: { required: false },
        d_dim_width_sidewalk: { required: false },
        d_num_of_span: { required: false },

        m_ss1_material: { required: false },
        m_ss1_structural_type_1: { required: false },
        m_ss1_structural_type_2: { required: false },
        m_ss1_num_of_girder: { required: false },
        m_ss1_num_of_slab: { required: false },
        m_ss2_material: { required: false },
        m_ss2_structural_type_1: { required: false },
        m_ss2_structural_type_2: { required: false },
        m_ss2_num_of_girder: { required: false },
        m_ss2_num_of_slab: { required: false },
        m_ss3_material: { required: false },
        m_ss3_structural_type_1: { required: false },
        m_ss3_structural_type_2: { required: false },
        m_ss3_num_of_girder: { required: false },
        m_ss3_num_of_slab: { required: false },
        m_sub_material: { required: false },
        m_sub_structural_type_of_pier: { required: false },
        m_sub_height_of_substructure: { required: false },
        m_sub_num_of_substructure: { required: false },

        bearing_with_or_without: { required: false },
        bearing_material: { required: false },
        ej_with_or_without: { required: false },
        ej_material: { required: false },
        railing_with_or_without: { required: false },
        railing_material: { required: false }
      },

      activeTab: 0 // Default to first tab
    }
  },

  props: {
    bridgeId: null
  },

  components: {
    VueTabs,
    VTab,
    Modal,
    RdbLookupDropdown
  },

  async mounted() {
    this.eligible = true;
    
    // Load structural soundness options first (localized)
    try {
      const res = await BridgesApi.structural_soundness_list();
      this.gi_structuralOptions = res && res.data ? res.data : [];
    } catch (e) {
      this.gi_structuralOptions = [];
    }

    // Load material options
    try {
      const mres = await BridgesApi.material_list();
      // Normalize option ids to strings to avoid type-mismatch between option values and model
      this.materialOptions = mres && mres.data ? (mres.data.map(o => ({ ...o, id: o.id !== undefined && o.id !== null ? String(o.id) : o.id }))) : [];
    } catch (e) {
      this.materialOptions = [];
    }

    // Load structural type 1 options
    try {
      const s1 = await BridgesApi.structural_type_1_list();
      this.structuralType1Options = s1 && s1.data ? (s1.data.map(o => ({ ...o, id: o.id !== undefined && o.id !== null ? String(o.id) : o.id }))) : [];
    } catch (e) {
      this.structuralType1Options = [];
    }

    // Load structural type 2 options
    try {
      const s2 = await BridgesApi.structural_type_2_list();
      this.structuralType2Options = s2 && s2.data ? (s2.data.map(o => ({ ...o, id: o.id !== undefined && o.id !== null ? String(o.id) : o.id }))) : [];
    } catch (e) {
      this.structuralType2Options = [];
    }

    // Load structural type of pier options
    try {
      const sp = await BridgesApi.structural_type_pier_list();
      this.structuralPierOptions = sp && sp.data ? (sp.data.map(o => ({ ...o, id: o.id !== undefined && o.id !== null ? String(o.id) : o.id }))) : [];
    } catch (e) {
      this.structuralPierOptions = [];
    }

    if (this.bridgeId) {
      const bridge = await this.getBridge(this.bridgeId);
      if (bridge) {
        // Map API response directly into text fields (revert to text inputs)
        this.gi_fk_region_oblast = bridge.region_name;
        this.gi_fk_deu = bridge.dep;
        // structural_soundness may be numeric id or localized text. Try to map to id.
        const ssValue = bridge.structural_soundness;
        if (ssValue === null || ssValue === undefined) {
          this.gi_fk_structural_soundness = null;
        } else if (typeof ssValue === 'number') {
          this.gi_fk_structural_soundness = ssValue;
        } else {
          // try numeric string
          const maybeNum = parseInt(ssValue);
          if (!isNaN(maybeNum)) {
            this.gi_fk_structural_soundness = maybeNum;
          } else {
            // try match by name (case-insensitive)
            const found = this.gi_structuralOptions.find(o => o.name && o.name.toString().trim().toLowerCase() === ssValue.toString().trim().toLowerCase());
            if (found) this.gi_fk_structural_soundness = found.id;
            else this.gi_fk_structural_soundness = null; // no match
          }
        }
        this.gi_maintenance_category = bridge.maintenance_category;
        this.gi_bridge_num = bridge.bridge_num;
        // Robust mapping: try to resolve material text to option id (exact, includes, startsWith)
        const findMaterialId = (val) => {
          if (val === null || val === undefined) return null;
          // if value is a number or numeric string, convert to string id to match normalized option ids
          const maybeNum = parseInt(val);
          if (!isNaN(maybeNum)) return String(maybeNum);
          const norm = (s) => s && s.toString().trim().toLowerCase();
          const vnorm = norm(val);
          if (!vnorm) return null;
          // exact match
          let found = this.materialOptions.find(o => norm(o.name) === vnorm);
          if (found) return String(found.id);
          // contains
          found = this.materialOptions.find(o => norm(o.name) && norm(o.name).includes(vnorm));
          if (found) return String(found.id);
          // startsWith
          found = this.materialOptions.find(o => norm(o.name) && norm(o.name).startsWith(vnorm));
          if (found) return String(found.id);
          // try reverse: value contains option name
          found = this.materialOptions.find(o => vnorm.includes(norm(o.name)));
          if (found) return String(found.id);
          return null;
        };
        this.m_ss1_material = findMaterialId(bridge.ss1_material) || this.m_ss1_material;
        this.m_ss2_material = findMaterialId(bridge.ss2_material) || this.m_ss2_material;
        this.m_ss3_material = findMaterialId(bridge.ss3_material) || this.m_ss3_material;
        this.gi_road_class = bridge.road_class;
        this.gi_year_construction = bridge.year_construction;
        this.gi_fk_road_route = bridge.route_name;
        this.gi_location = bridge.location;
        this.gi_longitude_e = bridge.longitude_e;
        this.gi_latitude_n = bridge.latitude_n;
        this.gi_design_load = bridge.design_load;
        this.gi_feature = bridge.feature;
        this.gi_attachment = bridge.attachment;

        this.d_dim_bridge_length = bridge.dim_length;
        this.d_bridge_length_count = bridge.bridge_length_count;
        this.d_dim_width_roadway = bridge.dim_width_roadway;
        this.d_dim_width_sidewalk = bridge.dim_width_sidewalk;
        this.d_num_of_span = bridge.num_of_span;

        // Map structural type 1/2 to ids (auto-select in dropdowns)
        const findOptionId = (options, val) => {
          if (!options || options.length === 0) return null;
          if (val === null || val === undefined) return null;
          const maybeNum = parseInt(val);
          if (!isNaN(maybeNum)) return String(maybeNum);
          const norm = (s) => s && s.toString().trim().toLowerCase();
          const vnorm = norm(val);
          if (!vnorm) return null;
          let found = options.find(o => norm(o.name) === vnorm);
          if (found) return String(found.id);
          found = options.find(o => norm(o.name) && norm(o.name).includes(vnorm));
          if (found) return String(found.id);
          found = options.find(o => norm(o.name) && norm(o.name).startsWith(vnorm));
          if (found) return String(found.id);
          found = options.find(o => vnorm.includes(norm(o.name)));
          if (found) return String(found.id);
          return null;
        };
        this.m_ss1_structural_type_1 = findOptionId(this.structuralType1Options, bridge.ss1_structural_type_1) || this.m_ss1_structural_type_1;
        this.m_ss1_structural_type_2 = findOptionId(this.structuralType2Options, bridge.ss1_structural_type_2) || this.m_ss1_structural_type_2;
        this.m_ss1_num_of_girder = bridge.ss1_num_of_girder;
        this.m_ss1_num_of_slab = bridge.ss1_num_of_slab;
        this.m_ss2_structural_type_1 = findOptionId(this.structuralType1Options, bridge.ss2_structural_type_1) || this.m_ss2_structural_type_1;
        this.m_ss2_structural_type_2 = findOptionId(this.structuralType2Options, bridge.ss2_structural_type_2) || this.m_ss2_structural_type_2;
        this.m_ss2_num_of_girder = bridge.ss2_num_of_girder;
        this.m_ss2_num_of_slab = bridge.ss2_num_of_slab;
        this.m_ss3_structural_type_1 = findOptionId(this.structuralType1Options, bridge.ss3_structural_type_1) || this.m_ss3_structural_type_1;
        this.m_ss3_structural_type_2 = findOptionId(this.structuralType2Options, bridge.ss3_structural_type_2) || this.m_ss3_structural_type_2;
        this.m_ss3_num_of_girder = bridge.ss3_num_of_girder;
        this.m_ss3_num_of_slab = bridge.ss3_num_of_slab;
        
        this.m_sub_material = findMaterialId(bridge.sub_material) || this.m_sub_material;
        
        this.m_sub_structural_type_of_pier = findOptionId(this.structuralPierOptions, bridge.sub_structural_type_of_pier) || bridge.sub_structural_type_of_pier;
        this.m_sub_height_of_substructure = bridge.sub_height_of_substructure;
        this.m_sub_num_of_substructure = bridge.sub_num_of_substructure;

        this.bearing_with_or_without = bridge.bearing_with_or_without;
        this.bearing_material = findMaterialId(bridge.bearing_material) || bridge.bearing_material;
        this.ej_with_or_without = bridge.ej_with_or_without;
        this.ej_material = findMaterialId(bridge.ej_material) || bridge.ej_material;
        this.railing_with_or_without = bridge.railing_with_or_without;
        this.railing_material = findMaterialId(bridge.railing_material) || bridge.railing_material;
      }
    }
    this.initialFormState = this.getCurrentState();
  },

  methods: {
    ...mapActions({
      addBridge: 'ADD_NEW_BRIDGE',
      updateBridge: 'UPDATE_BRIDGE',
      loadBridgeDetails: 'LOAD_BRIDGE_DETAILS' // root action mapping
    }),
    async getBridge(id) {
      // Use the correct action
      return await this.loadBridgeDetails(id);
    },
    getCurrentState() {
      // Return all fields as an object for saving
      return {
        gi_fk_region_oblast: this.gi_fk_region_oblast,
        gi_fk_deu: this.gi_fk_deu,
        gi_fk_structural_soundness: this.gi_fk_structural_soundness,
        gi_maintenance_category: this.gi_maintenance_category,
        gi_bridge_num: this.gi_bridge_num,
        gi_road_class: this.gi_road_class,
        gi_year_construction: this.gi_year_construction,
        gi_fk_road_route: this.gi_fk_road_route,
        gi_location: this.gi_location,
        gi_longitude_e: this.gi_longitude_e,
        gi_latitude_n: this.gi_latitude_n,
        gi_design_load: this.gi_design_load,
        gi_feature: this.gi_feature,
        gi_attachment: this.gi_attachment,
        d_dim_bridge_length: this.d_dim_bridge_length,
        d_bridge_length_count: this.d_bridge_length_count,
        d_dim_width_roadway: this.d_dim_width_roadway,
        d_dim_width_sidewalk: this.d_dim_width_sidewalk,
        d_num_of_span: this.d_num_of_span,
        m_ss1_material: this.m_ss1_material,
        m_ss1_structural_type_1: this.m_ss1_structural_type_1,
        m_ss1_structural_type_2: this.m_ss1_structural_type_2,
        m_ss1_num_of_girder: this.m_ss1_num_of_girder,
        m_ss1_num_of_slab: this.m_ss1_num_of_slab,
        m_ss2_material: this.m_ss2_material,
        m_ss2_structural_type_1: this.m_ss2_structural_type_1,
        m_ss2_structural_type_2: this.m_ss2_structural_type_2,
        m_ss2_num_of_girder: this.m_ss2_num_of_girder,
        m_ss2_num_of_slab: this.m_ss2_num_of_slab,
        m_ss3_material: this.m_ss3_material,
        m_ss3_structural_type_1: this.m_ss3_structural_type_1,
        m_ss3_structural_type_2: this.m_ss3_structural_type_2,
        m_ss3_num_of_girder: this.m_ss3_num_of_girder,
        m_ss3_num_of_slab: this.m_ss3_num_of_slab,
        m_sub_material: this.m_sub_material,
        m_sub_structural_type_of_pier: this.m_sub_structural_type_of_pier,
        m_sub_height_of_substructure: this.m_sub_height_of_substructure,
        m_sub_num_of_substructure: this.m_sub_num_of_substructure,
        bearing_with_or_without: this.bearing_with_or_without,
        bearing_material: this.bearing_material,
        ej_with_or_without: this.ej_with_or_without,
        ej_material: this.ej_material,
        railing_with_or_without: this.railing_with_or_without,
        railing_material: this.railing_material
      }
    },
    // Map component state to API payload expected by backend (Swagger)
    getApiPayload() {
      return {
        pluad_uad: this.pluad_uad || '',
        dep: this.gi_fk_deu,
        bridge_num: Number(this.gi_bridge_num) || 0,
        region_name: this.gi_fk_region_oblast,
        route_name: this.gi_fk_road_route,
        location: this.gi_location,
        structural_soundness: Number(this.gi_fk_structural_soundness) || 0,
        longitude_e: Number(this.gi_longitude_e) || 0,
        latitude_n: Number(this.gi_latitude_n) || 0,
        road_class: Number(this.gi_road_class) || 0,
        maintenance_category: this.gi_maintenance_category || '',
        feature: this.gi_feature || '',
        dim_length: Number(this.d_dim_bridge_length) || 0,
        bridge_length_count: Number(this.d_bridge_length_count) || 0,
        dim_width_roadway: Number(this.d_dim_width_roadway) || 0,
        dim_width_sidewalk: Number(this.d_dim_width_sidewalk) || 0,
        num_of_span: Number(this.d_num_of_span) || 0,

        ss1_material: Number(this.m_ss1_material) || 0,
        ss1_structural_type_1: Number(this.m_ss1_structural_type_1) || 0,
        ss1_structural_type_2: Number(this.m_ss1_structural_type_2) || 0,
        ss1_num_of_girder: Number(this.m_ss1_num_of_girder) || 0,
        ss1_num_of_slab: Number(this.m_ss1_num_of_slab) || 0,

        ss2_material: Number(this.m_ss2_material) || 0,
        ss2_structural_type_1: Number(this.m_ss2_structural_type_1) || 0,
        ss2_structural_type_2: Number(this.m_ss2_structural_type_2) || 0,
        ss2_num_of_girder: Number(this.m_ss2_num_of_girder) || 0,
        ss2_num_of_slab: Number(this.m_ss2_num_of_slab) || 0,

        ss3_material: Number(this.m_ss3_material) || 0,
        ss3_structural_type_1: Number(this.m_ss3_structural_type_1) || 0,
        ss3_structural_type_2: Number(this.m_ss3_structural_type_2) || 0,
        ss3_num_of_girder: Number(this.m_ss3_num_of_girder) || 0,
        ss3_num_of_slab: Number(this.m_ss3_num_of_slab) || 0,

        sub_material: Number(this.m_sub_material) || 0,
        sub_structural_type_of_pier: Number(this.m_sub_structural_type_of_pier) || 0,
        sub_height_of_substructure: Number(this.m_sub_height_of_substructure) || 0,
        sub_num_of_substructure: Number(this.m_sub_num_of_substructure) || 0,

        bearing_with_or_without: Number(this.bearing_with_or_without) || 0,
        bearing_material: Number(this.bearing_material) || 0,
        ej_with_or_without: Number(this.ej_with_or_without) || 0,
        ej_material: Number(this.ej_material) || 0,
        railing_with_or_without: Number(this.railing_with_or_without) || 0,
        railing_material: Number(this.railing_material) || 0,

        design_load: this.gi_design_load || '',
        year_construction: Number(this.gi_year_construction) || 0,
        year_last_repair: 0,
        attachment: this.gi_attachment || '',
        remark: '',
        repair_rate: '',
        replacement: '',
        repair_crack: '',
        repair_cross_sectional: '',
        repair_steel_panel: '',
        total_cost_thousand_kgs: 0,
        kilopost: 0,
        construction_year_count: 0
      }
    },
    async validate() {
      // Validate year before sending
      if (this.gi_year_construction && !this.isYearValid) {
        await errorMessage(this.yearErrorMessage || this.tOr('errors.invalid_year', 'Please enter a valid year (1900-2100)'));
        return;
      }
      // Validate latitude before sending
      if ((this.gi_latitude_n || this.gi_latitude_n === 0) && !this.isLatitudeValid) {
        await errorMessage(this.latitudeErrorMessage || this.tOr('errors.invalid_latitude', 'Please enter a valid latitude (-90 to 90)'));
        return;
      }
      // Validate longitude before sending
      if ((this.gi_longitude_e || this.gi_longitude_e === 0) && !this.isLongitudeValid) {
        await errorMessage(this.longitudeErrorMessage || this.tOr('errors.invalid_longitude', 'Please enter a valid longitude (-180 to 180)'));
        return;
      }
      const payload = this.getApiPayload();
      let errDesc = '';
      try {
        if (this.bridgeId) {
          await this.updateBridge({ id: this.bridgeId, theItem: payload });
        } else {
          await this.addBridge(payload);
        }
        // Notify parent to refresh table
        this.$emit('saved');
        // Close modal only on success
        this.$emit('close');
        await savedMessage(null, this.$t('map.bridges'), this.gi_bridge_num);
        return;
      } catch (err) {
        errDesc = err.message || this.$t('errors.unknown_error');
      }
      // On error keep the modal open and show an error dialog
      await errorMessage(errDesc);
    }

    ,
    // helper: translation or fallback
    tOr(key, fallback) {
      try {
        const res = this.$t(key);
        // vue-i18n returns the key when translation is missing; fall back in that case
        if (!res || res === key) return fallback;
        return res;
      } catch (e) {
        return fallback;
      }
    }
  },

  computed: {
    // Year validation computed property similar to tunnels form
    isYearValid() {
      const year = this.gi_year_construction;
      if (!year) return true; // optional
      const yearStr = year.toString();
      const yearNum = parseInt(year);
      return yearStr.length === 4 && yearNum >= 1900 && yearNum <= 2100 && !isNaN(yearNum);
    },
    yearErrorMessage() {
      if (!this.gi_year_construction) return '';
      if (!this.isYearValid) {
        const year = this.gi_year_construction.toString();
        if (year.length !== 4) return this.tOr('errors.year_4_digits', 'Year must be exactly 4 digits');
        return this.tOr('errors.year_range', 'Year must be between 1900 and 2100');
      }
      return '';
    },
    isLatitudeValid() {
      const lat = this.gi_latitude_n;
      if (!lat && lat !== 0) return true; // optional field
      const latNum = parseFloat(lat);
      return !isNaN(latNum) && latNum >= -90 && latNum <= 90;
    },
    latitudeErrorMessage() {
      if (!this.gi_latitude_n && this.gi_latitude_n !== 0) return '';
      if (!this.isLatitudeValid) {
        return this.tOr('errors.latitude_range', 'Latitude must be between -90 and 90 degrees');
      }
      return '';
    },
    isLongitudeValid() {
      const lng = this.gi_longitude_e;
      if (!lng && lng !== 0) return true; // optional field
      const lngNum = parseFloat(lng);
      return !isNaN(lngNum) && lngNum >= -180 && lngNum <= 180;
    },
    longitudeErrorMessage() {
      if (!this.gi_longitude_e && this.gi_longitude_e !== 0) return '';
      if (!this.isLongitudeValid) {
        return this.tOr('errors.longitude_range', 'Longitude must be between -180 and 180 degrees');
      }
      return '';
    }
    ,
    screenTitle() {
      return this.bridgeId ? this.$t('screen_titles.bridge_upd') : this.$t('screen_titles.bridge_add');
    },
    hasUnsavedChanges() {
      return false;
    }
  },

  watch: {
    gi_year_construction(newValue) {
      if (newValue) {
        let cleanValue = newValue.toString().replace(/\D/g, '').substring(0, 4);
        if (cleanValue !== newValue.toString()) {
          this.$nextTick(() => {
            this.gi_year_construction = cleanValue;
          });
        }
      }
    },
    gi_latitude_n(newValue) {
      if (newValue !== null && newValue !== undefined && newValue !== '') {
        // Allow decimal numbers with optional minus sign
        let cleanValue = newValue.toString().replace(/[^-0-9.]/g, '');
        // Ensure only one decimal point and one minus sign at start
        const parts = cleanValue.split('.');
        if (parts.length > 2) {
          cleanValue = parts[0] + '.' + parts.slice(1).join('');
        }
        if (cleanValue.indexOf('-') > 0) {
          cleanValue = cleanValue.replace(/-/g, '');
          if (newValue.toString().startsWith('-')) {
            cleanValue = '-' + cleanValue;
          }
        }
        if (cleanValue !== newValue.toString()) {
          this.$nextTick(() => {
            this.gi_latitude_n = cleanValue;
          });
        }
      }
    },
    gi_longitude_e(newValue) {
      if (newValue !== null && newValue !== undefined && newValue !== '') {
        // Allow decimal numbers with optional minus sign
        let cleanValue = newValue.toString().replace(/[^-0-9.]/g, '');
        // Ensure only one decimal point and one minus sign at start
        const parts = cleanValue.split('.');
        if (parts.length > 2) {
          cleanValue = parts[0] + '.' + parts.slice(1).join('');
        }
        if (cleanValue.indexOf('-') > 0) {
          cleanValue = cleanValue.replace(/-/g, '');
          if (newValue.toString().startsWith('-')) {
            cleanValue = '-' + cleanValue;
          }
        }
        if (cleanValue !== newValue.toString()) {
          this.$nextTick(() => {
            this.gi_longitude_e = cleanValue;
          });
        }
      }
    }
  }

  
}
</script>

<style>
.md-card {
  margin: 0px 0;
}

.md-button+.md-button {
  margin-left: 10px;
}

/* Outer modal (popup itself) */


/* Inner modal content */

.modal-container {
  margin: 0 auto;
  background: #fff;
  box-sizing: border-box;
  border-radius: 8px;
  padding: 16px 12px;
  overflow-x: auto;
  box-shadow: none !important;
  max-width: 1300px;
  min-width: 1300px;
  overflow-x: hidden;
}

.modal-default {
  max-width: 1300px;
  min-width: 700px;
}

.modal-dimensions {
  width: 500px;
  min-width: 400px;
  max-width: 98vw;
}

.modal-dimensions {
  width: 500px;
  min-width: 400px;
  max-width: 98vw;
}

.vue-tabs .nav-tabs>li.active>a,
.vue-tabs .nav-tabs>li.active>a:hover,
.vue-tabs .nav-tabs>li.active>a:focus {
  background-color: #4caf50;
}

.materials-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.materials-table td {
  padding: 4px 2px;
  text-align: left;
  vertical-align: middle;
}

.materials-table td:first-child {
  width: 12%;
  font-weight: 500;
  padding-right: 8px;
}

.materials-table td:nth-child(2),
.materials-table td:nth-child(3),
.materials-table td:nth-child(4) {
  width: 20%;
}

.materials-table td:nth-child(5),
.materials-table td:nth-child(6) {
  width: 14%;
}

.materials-table .md-field {
  margin: 0;
  min-height: 48px;
}

.other-parts-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.md-layout {
  width: 100%;
}

.other-parts-table-wrapper {
  width: 100%;
}

.other-parts-table th,
.other-parts-table td {
  /* reduced padding to tighten columns */
  padding: 6px 8px;
  text-align: left;
  vertical-align: middle;
}

.other-parts-table th:first-child,
.other-parts-table td:first-child {
  width: 10%;
  padding-left: 12px;
}

.other-parts-table th:nth-child(2),
.other-parts-table td:nth-child(2) {
  width: 30%;
}

.other-parts-table th:nth-child(2),
.other-parts-table td:nth-child(2) {
  width: 22%;
}

.other-parts-table th:nth-child(2),
.other-parts-table td:nth-child(2) {
  width: 16%;
}

.other-parts-table th:nth-child(3),
.other-parts-table td:nth-child(3) {
  width: 60%;
}

/* further tighten spacing for inputs */
.other-parts-table td .md-input,
.other-parts-table td .md-select {
  margin: 0 !important;
  padding: 0 !important;
}

/* cap select width so it doesn't stretch the whole modal */
.other-parts-table td .md-field .md-select {
  max-width: 280px;
  width: 100%;
  box-sizing: border-box;
}

/* ensure md-field inside other-parts cells doesn't add extra left spacing */
.other-parts-table td .md-field {
  margin-left: 0 !important;
  padding: 0 !important;
  /* keep the field inline and capped so the underline doesn't span the full table */
  width: auto;
  max-width: 280px;
  display: inline-block;
  min-width: 0;
}

.materials-table th:nth-child(3),
.materials-table td:nth-child(3) {
  width: 16%;
  text-align: left;
}

.materials-table th:nth-child(4),
.materials-table td:nth-child(4) {
  width: 16%;
  text-align: left;
}

.materials-table th:nth-child(5),
.materials-table td:nth-child(5) {
  width: 17%;
  text-align: left;
}

.materials-table th:nth-child(6),
.materials-table td:nth-child(6) {
  width: 17%;
  text-align: left;
}

/* Keep original materials-table spacing intact to avoid breaking Tab 3 */
.materials-table .md-field {
  width: 100%;
  min-width: 0;
  max-width: 100px;
}

.substructure-row {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  justify-content: flex-start;
}

.substructure-row span {
  min-width: 140px;
  text-align: left;
  margin-right: 16px;
}

.substructure-row .md-field {
  width: 250px;
}

.dimensions-section {
  width: 100%;
  margin-top: 32px;
}

.dimension-row {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
  justify-content: flex-start;
}

.dimension-row span {
  min-width: 220px;
  text-align: left;
  margin-right: 16px;
  font-weight: 500;
}

.dimension-row .md-field {
  width: 260px;
}

.general-info-section {
  width: 100%;
  margin-top: 32px;
}

.general-row-pair {
  display: flex;
  flex-direction: row;
  gap: 32px;
  margin-bottom: 10px;
}

.general-row {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 12px;
}

.general-row span {
  min-width: 140px;
  text-align: left;
  font-weight: 500;
}

.general-row .md-field {
  width: 100%;
}

.general-row {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 12px;
}

.general-row span {
  min-width: 140px;
  text-align: left;
  font-weight: 500;
}

.general-row .md-field {
  width: 100%;
}


/* New label/input column styles for general info tab */
.general-row-pair {
  display: grid;
  grid-template-columns: 1fr 2.2fr 1fr 2.2fr;
  gap: 24px;
  margin-bottom: 10px;
}

.general-label {
  display: flex;
  align-items: center;
  min-width: 140px;
  font-weight: 500;
  text-align: left;
}

.general-input {
  display: flex;
  align-items: center;
  width: 100%;
}

.general-input .md-field {
  width: 100%;
  min-width: 180px;
  max-width: 100%;
}

/* Bridges form styling for proper spacing and margins */
.bridges-form {
  padding-right: 24px !important;
  margin-bottom: 6px;
}

.bridges-form .md-layout-item {
  margin-bottom: 8px;
}

.bridges-form .md-field {
  margin-bottom: 2px;
  margin-top: 0px;
}

.bridges-form .md-layout-item .md-field {
  min-height: 56px;
}
</style>